import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import injectSheet from 'react-jss';

import { sendForgotPasswordEmail } from '~/api/login';
import {
  black2,
  evisortRed,
  evisortYellow,
  semibold,
} from '~/assets/shared-styles/general';
import LoginPageContainer from '~/components/LoginPage/LoginPageContainer/LoginPageContainer';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { useAsync } from '~/hooks/useAsync';
import Alert from '~/ui/components/Alert/Alert';
import { isEmailValid } from '~/utils/helper.utils';

function Page(props) {
  const { classes, intl } = props;

  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState();
  const [workspace, handleWorkspaceChange] = useState();
  const [email, handleEmailChange] = useState();

  const validEmail = isEmailValid(email);

  const { executor: handleForgotPasswordEmail, isLoading } = useAsync(
    sendForgotPasswordEmail,
    { client: workspace, userId: email },
    {
      successHandler: () => {
        setEmailSent(true);
      },
      errorHandler: () => {
        setError(true);
      },
    },
  );

  return (
    <div>
      {emailSent ? (
        <LoginPageContainer
          classes={classes}
          title={intl.formatMessage({
            id: 'ExpiredTokenPage.checkInboxTitle',
            defaultMessage: 'Check your inbox',
            description: 'Title when email has been sent',
          })}
        >
          <Alert enableIcon variant="success">
            {intl.formatMessage({
              id: 'ExpiredTokenPage.emailSentMessage',
              defaultMessage:
                'You will receive reset link shortly. You may need to check your spam folder or unblock evisort@evisort.com',
              description: 'Success message after email is sent',
            })}
          </Alert>
        </LoginPageContainer>
      ) : (
        <LoginPageContainer
          classes={classes}
          title={intl.formatMessage({
            id: 'ExpiredTokenPage.linkExpiredTitle',
            defaultMessage: 'Link has expired',
            description: 'Title when token link has expired',
          })}
        >
          <div className={classes.contentCopy}>
            {intl.formatMessage({
              id: 'ExpiredTokenPage.instructionMessage',
              defaultMessage:
                'Enter your workspace and email to receive a new link. You may need to check your spam folder or unblock evisort@evisort.com',
              description: 'Instructions for getting a new reset link',
            })}
          </div>
          {error && (
            <div className={classes.errorMessage}>
              <Alert enableIcon variant="danger">
                {intl.formatMessage({
                  id: 'ExpiredTokenPage.errorMessage',
                  defaultMessage: 'An error ocurred. Please try again.',
                  description: 'Error message when request fails',
                })}
              </Alert>
            </div>
          )}
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleForgotPasswordEmail}>
              <div className={classes.rowContainer}>
                <label className={classes.loginFormLabel} htmlFor="workspace">
                  {intl.formatMessage({
                    id: 'ExpiredTokenPage.workspaceLabel',
                    defaultMessage: 'Workspace',
                    description: 'Label for workspace input field',
                  })}
                </label>
                <input
                  data-testid="login/reset-password__token-expired-workspace"
                  type="text"
                  onChange={(e) => handleWorkspaceChange(e.target.value)}
                  className={classes.textInput}
                />
              </div>
              <div className={classes.rowContainer}>
                <label className={classes.loginFormLabel} htmlFor="email">
                  {intl.formatMessage({
                    id: 'ExpiredTokenPage.emailLabel',
                    defaultMessage: 'Email',
                    description: 'Label for email input field',
                  })}
                </label>
                <input
                  data-testid="login/reset-password__token-expired-email"
                  type="text"
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className={classes.textInput}
                />
              </div>
              <div className={classes.formButtonContainer}>
                <button
                  type="submit"
                  className={classes.formButton}
                  onClick={handleForgotPasswordEmail}
                  disabled={!workspace || !validEmail}
                >
                  {intl.formatMessage({
                    id: 'ExpiredTokenPage.sendNewLinkButton',
                    defaultMessage: 'Send new link',
                    description: 'Button text to send new reset link',
                  })}
                </button>
              </div>
            </form>
          )}
        </LoginPageContainer>
      )}
    </div>
  );
}

Page.propTypes = {
  classes: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
};

const formButton = {
  padding: '10px 20px',
  width: '360px',
  display: 'inline-block',
  backgroundColor: evisortYellow,
  border: 'solid 1px rgba(0, 0, 0, 0.25)',
  borderRadius: '3px',
  color: black2,
  fontSize: '15px',
  cursor: 'pointer',
  textDecoration: 'none',
  '&:disabled': {
    opacity: 0.5,
  },
};

const styles = {
  rowContainer: {
    marginBottom: '24px',
    position: 'relative',
    display: 'inline-block',
  },
  formButtonContainer: {
    marginTop: '20px',
  },
  formButton: {
    ...formButton,
  },
  loginFormLabel: {
    display: 'block',
    fontSize: '15px',
    fontWeight: semibold,
    textAlign: 'left',
  },
  textInput: {
    margin: '10px 0',
    padding: '10px',
    width: '360px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    color: black2,
    fontSize: '16px',
    '&:placeholder': {
      opacity: 0.5,
    },
    '&:focus': {
      outline: 'none',
    },
  },
  contentCopy: {
    margin: '0 auto 30px',
    width: '360px',
    color: black2,
    fontSize: '15px',
    textAlign: 'left',
    lineHeight: '24px',
  },
  errorMessage: {
    margin: '20px',
    color: evisortRed,
    fontSize: '15px',
  },
};

export default injectSheet(styles)(injectIntl(Page));
